import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import SchoolProgramReportRow from "./SchoolProgramReportRow";
import LinearProgress from "@material-ui/core/LinearProgress";
import {schoolProgramHeaders} from "./SchoolProgramTableHeaders"

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}





function EnhancedReportTableHead(props) {
  const { order, orderBy, onRequestSort, showActions, headCellClass } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

//   const addAccessCols = () => {
//     if (classRows[classRows.length - 1].id != "actions") {
//       classRows = [
//         ...classRows,
//         {
//           id: "actions",
//           numeric: false,
//           disablePadding: false,
//           label: "Actions",
//         },
//       ];
//     }
//   };
//   if (showActions) {
//     addAccessCols();
//   }

  return (
    <TableHead>
      <TableRow>
        {schoolProgramHeaders.map((row, index) => (
          <TableCell
            key={`${index}${row.id}`}
            className={headCellClass}
            align="center"
            // sortDirection={orderBy === row.id ? order : false}
          >
            {row.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedReportTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  regularClassIcon: {
    color: "#9f7431",
  },
  table: {
    minWidth: 750,
  },
  // 17 columns, so the table is roughly twice the card's width. Bound the
  // wrapper's height and let it scroll both ways so the horizontal bar sits at
  // the bottom of the visible area instead of below ten tall rows, and draw the
  // bars explicitly: macOS overlay scrollbars stay hidden until a scroll starts,
  // which read as "the scroll bar is not working".
  tableWrapper: {
    overflow: "auto",
    maxHeight: "70vh",
    "&::-webkit-scrollbar": {
      WebkitAppearance: "none",
      height: 10,
      width: 10,
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 5,
      backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgba(0, 0, 0, 0.06)",
    },
  },
  // MUI v4's stickyHeader cell also sets `left: 0`, which pins every header to
  // the left edge once the wrapper scrolls sideways; headers must only stick
  // vertically here.
  stickyHead: {
    left: "auto",
  },
}));

export default function EnhancedReportTable({ reports, showActions }) {
  const classes = useStyles();
  const rows = reports;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [accordianReset, setAccordianReset] = useState(false);

  function handleRequestSort(event, property) {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
    setAccordianReset(true);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
  }

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows?.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            stickyHeader
          >
            <EnhancedReportTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows ? rows.length : 0}
              showActions={showActions}
              headCellClass={classes.stickyHead}
            />
              <TableBody>
                {rows &&
                  stableSort(rows, getSorting(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                        <SchoolProgramReportRow
                          // Enrolled rows key on student_id so their expand state
                          // survives re-sorting/paging. Pending (web-transaction)
                          // rows have no student_id yet, so they fall back to the
                          // slice index to keep the key unique.
                          key={`${row.student_id || `pending-${index}`}${row.course_abbr}${row.school_program_id}${row.day}`}
                          row={row}
                          reset={accordianReset}
                          setReset={setAccordianReset}
                          showActions={showActions}
                        />
                      );
                    })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 49 * emptyRows }}>
                    <TableCell colSpan={7} />
                  </TableRow>
                )}
              </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows ? rows.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            "aria-label": "Previous Page",
          }}
          nextIconButtonProps={{
            "aria-label": "Next Page",
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
